import { Alert, Button, Col, HeadingText, Row } from '@paypalcorp/pp-react'
import React, { useCallback, useEffect, useState } from 'react'

import { Message } from '@paypalcorp/worldready-react'
import useTracking from '../../hooks/useTracking'
import { componentNames, fptiTypes } from '../../utils/analytics/fpti-constants'
import { dataMasking } from '../../utils/helper'
import { ARTICLE_FEEDBACK_DATA } from '../../utils/prop-type-shape'
import ArticleFeedbackReason from '../article-feedback-reason'
import './article-feedback.less'

const { YES, NO } = {
  YES: 'Y',
  NO: 'N',
}

const HCAR_MAPPINGS = {
  Y: 'Y',
  toomuchinfo: 'N1',
  notenoughinfo: 'N2',
  lookingfor: 'N3',
  incorrect: 'N4',
  dontlike: 'N5',
  other: 'N99',
}

function ArticleFeedback({ articleId, helpComponentIds }) {
  const [isFbCaptured, setIsFbCaptured] = useState(false)
  const [isReasonRequired, setIsReasonRequired] = useState(false)
  const { trackClick } = useTracking()

  const closeFbReasonForm = useCallback(() => {
    setIsReasonRequired(false)
  }, [setIsReasonRequired])

  useEffect(() => {
    if (isReasonRequired) {
      document.addEventListener('click', closeFbReasonForm)
    } else {
      document.removeEventListener('click', closeFbReasonForm)
    }
  }, [isReasonRequired, closeFbReasonForm])

  const captureFeedback = (reason, reasonDesc) => {
    setIsFbCaptured(true)
    setIsReasonRequired(false)
    const hcar = HCAR_MAPPINGS[reason]
    let hcaf = `${articleId}|${hcar}`
    if (reasonDesc) {
      hcaf += `|${dataMasking(reasonDesc)}`
    }
    trackClick({
      name: componentNames.ARTICLE_FEEDBACK,
      type: fptiTypes.ON_CLICK,
      hcar,
      hcaf,
      help_doc_id: articleId,
      help_component_ids: helpComponentIds,
      link: `${componentNames.ARTICLE_FEEDBACK}|${articleId}|feedback`,
    })
  }

  const fbAnswerHandler = (event, answer) => {
    event.stopPropagation()
    if (answer === NO) {
      setIsReasonRequired(true)
      return
    }
    captureFeedback(answer)
  }

  const renderFbSection = () => {
    if (!isFbCaptured) {
      return (
        <>
          <Col xs={12} sm={7} md={6} lg={7}>
            <HeadingText
              data-testid="hc-article-fb-heading"
              className="hc_article_fb__heading"
              size="sm"
            >
              <Message id="app.article.feedback.header" />
            </HeadingText>
          </Col>
          <Col xs={12} sm={5} md={6} lg={5} className="hc_article_fb__answer">
            <Button
              data-testid="hc-article-fb-btn-yes"
              className="hc_article_fb__answer--spacing"
              onClick={(e) => fbAnswerHandler(e, YES)}
              fullWidthOnMobile={false}
              secondary
            >
              <Message id="app.article.feedback.select.yes" />
            </Button>
            <Button
              fullWidthOnMobile={false}
              data-testid="hc-article-fb-btn-no"
              onClick={(e) => fbAnswerHandler(e, NO)}
              secondary
            >
              <Message id="app.article.feedback.select.no" />
            </Button>
          </Col>
        </>
      )
    }

    return null
  }

  const renderReasonCaptureBlock = () => {
    if (!isFbCaptured && isReasonRequired) {
      return (
        <Col
          data-testid="hc-article-fb-reason-card"
          xs={12}
          className="hc_article_fb_reason__section"
        >
          <div
            className="hc_article_fb_reason__card"
            onClick={(e) => e.stopPropagation()}
          >
            <ArticleFeedbackReason onSubmit={captureFeedback} />
          </div>
        </Col>
      )
    }
    return null
  }

  const renderFbRecieved = () => {
    if (isFbCaptured) {
      return (
        <Col xs={12} sm={9} lg={6}>
          <Alert type="success" data-testid="hc-article-fb-greeting-success">
            <Message id="app.article.feedback.greeting.success" />
          </Alert>
        </Col>
      )
    }

    return null
  }

  return (
    <Row data-testid="hc-article-fb" className="hc_article_fb">
      {renderFbRecieved()}
      {renderFbSection()}
      {renderReasonCaptureBlock()}
    </Row>
  )
}

ArticleFeedback.propTypes = ARTICLE_FEEDBACK_DATA

export default ArticleFeedback
